import { Card, CardContent } from '@/components/ui/card';
import { FileText, CheckCircle, XCircle, Clock } from 'lucide-react';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

interface EstimateSummaryCardsProps {
  stats: {
    sent: { count: number; total: number };
    accepted: { count: number; total: number };
    declined: { count: number; total: number };
    expiringSoon: number;
    acceptanceRate: number;
  } | undefined;
  isLoading?: boolean;
}

export function EstimateSummaryCards({ stats, isLoading }: EstimateSummaryCardsProps) {
  const { locale } = useCompanyLocale();
  // Whole-currency figures for KPI tiles ("$12,400"), in the tenant's currency
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat(locale.locale, {
      style: 'currency',
      currency: locale.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  const cards = [
    {
      title: 'Sent',
      value: stats ? formatCurrency(stats.sent.total) : formatCurrency(0),
      subtitle: stats ? `${stats.sent.count} estimates` : '0 estimates',
      icon: FileText,
      iconColor: 'text-info',
      bgColor: 'bg-info/10',
    },
    {
      title: 'Accepted',
      value: stats ? formatCurrency(stats.accepted.total) : formatCurrency(0),
      subtitle: stats ? `${stats.accepted.count} (${stats.acceptanceRate}%)` : '0 (0%)',
      icon: CheckCircle,
      iconColor: 'text-success',
      bgColor: 'bg-success/10',
    },
    {
      title: 'Declined',
      value: stats ? formatCurrency(stats.declined.total) : formatCurrency(0),
      subtitle: stats ? `${stats.declined.count} estimates` : '0 estimates',
      icon: XCircle,
      iconColor: 'text-destructive',
      bgColor: 'bg-destructive/10',
    },
    {
      title: 'Expiring Soon',
      value: stats ? `${stats.expiringSoon}` : '0',
      subtitle: 'within 3 days',
      icon: Clock,
      iconColor: 'text-warning',
      bgColor: 'bg-warning/10',
    },
  ];

  return (
    <div className="grid gap-4 md:grid-cols-4">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <Card key={card.title}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-medium text-muted-foreground">{card.title}</p>
                  <p className="mt-1.5 text-[26px] font-semibold leading-none tracking-tight tabular-nums">
                    {isLoading ? '...' : card.value}
                  </p>
                  <p className="mt-1.5 text-xs text-muted-foreground tabular-nums">
                    {isLoading ? '' : card.subtitle}
                  </p>
                </div>
                <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${card.bgColor}`}>
                  <Icon className={`h-4 w-4 ${card.iconColor}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
